import { useState, useCallback, useEffect } from 'react';

import { UseDataProps, ItemProps } from '../type';
import { getLocaleText } from '@/pages/home/components/RegionSelect/locale'
import {
  GLOBAL,
  GLOBAL_NO_CHINA,
  EMPTY,
  HALF,
  ALL,
  COMMON,
  CUSTOMIZED,
} from '../../contants';

const useData = (props: UseDataProps) => {
  const {
    value,
    defaultValue,
    activeTab,
    regionType,
    regions,
    deleteFlag,
    recentList: recentListProps,
    globalNodataText,
    unidentifiedNoDataText,
    onTabChange,
    onChange,
  } = props;
  const tabs = [
    {
      id: COMMON,
      label: getLocaleText('tab_common'),
    },
    {
      id: CUSTOMIZED,
      label: getLocaleText('tab_popular'),
    },
  ];
  const [initFlag, setInitFlag] = useState(false);
  const [isSetDefault, setIsSetDefault] = useState(false);
  const [activeTabId, setActiveTabId] = useState(activeTab ?? tabs[0]?.id);
  const [renderList, setRenderList] = useState({} as any);
  const [recentList, setRecentList] = useState([] as any);
  const [activeItem, setActiveItem] = useState({} as any);
  const [selectList, setSelectList] = useState({
    global: GLOBAL,
  } as any);
  const [searchValue, setSearchValue] = useState('');
  const [result, setResult] = useState({} as any);

  const init = (initValue?: string, isMarketClearall?: boolean) => {
    selectListInit(initValue || null);
    commonListInit(isMarketClearall);
    recentListInit();
    if (!initFlag) {
      setInitFlag(true);
    }
  };

  // 初始化 resultList
  const selectListInit = (initValue: string | null) => {
    if (initValue) {
      initValue = initValue?.toLocaleLowerCase()
    }
    setSelectList({
      global: initValue || GLOBAL,
    });
  };

  // 渲染数据初始化
  const commonListInit = (isMarketClearall?: boolean) => {
    const global = [
      {
        id: GLOBAL,
        name: getLocaleText('global'),
        cnname: '全部',
      },
      {
        id: GLOBAL_NO_CHINA,
        name: getLocaleText('global_except_china'),
        cnname: '全球除中国大陆',
      },
    ];

    // 初始化 regions
    const newRegions = (() => {
      const newRegionResult = regions?.map((region: any) => ({
        ...region,
        childrenSelectList: [],
        selectStatus: EMPTY,
      }));
      return newRegionResult || [];
    })();

    setRenderList({
      global,
      regions: newRegions,
    });
    if (isMarketClearall) return; // 如果是markets操作Clear All，激活当前region，不要激活第一个region
    setActiveItem(newRegions[0]);
  };

  // recent 数据初始化
  const recentListInit = () => {
    setRecentList(recentListProps || []);
  };

  // 查询
  const search = (value: string) => {
    setSearchValue(value?.toLowerCase());
  };

  const recentListOnChange = useCallback(
    (recentSelectList: string[]) => {
      setSelectList({
        ...selectList,
        markets: recentSelectList,
      });
      const regions = renderList?.regions?.map((region: any) => {
        const childrenSelectList: any[] = [];
        region?.children?.forEach((market: any) => {
          if (recentSelectList?.includes(market?.id)) {
            childrenSelectList.push(market?.id);
          }
        });
        return {
          ...region,
          childrenSelectList,
        };
      });
      setRenderList({
        ...renderList,
        regions,
      });
    },
    [selectList],
  );

  // 标签删除事件
  const tagOnDelete = useCallback(
    (deleteIds?: string[], deleteAll: boolean = false) => {
      if (deleteAll) {
        init();
        return;
      }
      if (activeTabId === CUSTOMIZED) {
        recentListOnChange(selectList?.markets?.filter((item: any) => item !== deleteIds?.[0]))
        return;
      }
      let parentId: any = null;
      let childrenSelectList: any = [];

      for (let i = 0; i < renderList?.regions?.length; i++) {
        const region = renderList?.regions[i];

        for (let j = 0; j < region?.childrenSelectList?.length; j++) {
          const marketId = region?.childrenSelectList[j];
          if (deleteIds?.includes(marketId)) {
            parentId = region?.id;
            const index = region?.childrenSelectList?.findIndex(
              (item: any) => item === deleteIds[0],
            );
            childrenSelectList = [...region?.childrenSelectList];
            childrenSelectList.splice(index, 1);
            break;
          }
        }
        if (parentId) {
          childrenSelectList = childrenSelectList;
        }
      }
      marketsSelectListOnChange(childrenSelectList, parentId);
    },
    [renderList, activeItem],
  );

  // 当前激活元素 change
  const activeItemOnChange = (item: ItemProps) => {
    const newActiveItem = item;
    if (item?.children?.length === 0) {
      if (item?.id === 'global') {
        newActiveItem.noDataText = globalNodataText;
      } else if (item?.id === 'global_wo_china') {
        newActiveItem.noDataText = 'Including comments from game store and social media, except which from china mainland.';
      } else if (item?.id === 'Unidentified') {
        newActiveItem.noDataText = unidentifiedNoDataText;
      }
    }
    setActiveItem(newActiveItem);
  };

  // 改变选中状态
  const changeSelectStatus = useCallback(
    (originObj: any, childrenSelectList: string[]) => {
      // 选中一部分
      const isHalfSelect =
        originObj?.children?.length > 0 &&
        originObj?.children?.length > childrenSelectList.length;
      // 选择为空
      const isEmptySelect = childrenSelectList.length <= 0;

      if (isEmptySelect) {
        // 当子列表选择为空时
        originObj.selectStatus = EMPTY;
        const regions = selectList?.regions?.filter(
          (region: any) => region !== originObj?.id,
        );

        setSelectList({
          ...selectList,
          regions,
        });
      } else if (isHalfSelect) {
        // 当子列表选择一部分时
        originObj.selectStatus = HALF;
      } else {
        // 当子列表选择全部时
        if (!selectList?.regions?.includes(originObj?.id)) {
          if (!selectList.regions) selectList.regions = [];
          selectList.regions.push(originObj.id);

          setSelectList({ ...selectList });
        }
        originObj.selectStatus = ALL;
      }
    },
    [selectList],
  );

  // global 切换事件
  const globalSelectOnChange = (value: any) => {
    setSelectList({
      ...selectList,
      global: value,
      regions: [],
    });
    // 重新初始化 region 及 activeItem
    commonListInit(true); // 传true是为了不激活第一个region
  };

  // region selectList 变化
  const regionSelectListOnChange = useCallback(
    (regions: any = [], forceClear?: boolean, context?: object) => {
      if (forceClear) {
        init();
        return;
      }
      let global = selectList?.global || '';
      const halfSelectLength = renderList?.regions?.filter(
        (region: any) => region?.selectStatus === HALF,
      );

      // 若有 region 选择则取消 global
      if (regions.length > 0 || halfSelectLength?.length > 0) {
        global = '';
      } else {
        global = GLOBAL;
      }

      const newRegions = renderList?.regions?.map((region: any) => {
        // 清空子选择列表
        let childrenSelectList: any[] = [];
        if (region?.selectStatus === HALF) {
          childrenSelectList = region?.childrenSelectList;
        }

        regions?.forEach((regionId: string) => {
          if (regionId === region?.id) {
            // 选择全部子节点
            childrenSelectList = region?.children?.map((item: any) => item?.id);
            region.selectStatus = ALL;
          }
        });
        // 取消某个region
        if (region?.id === context?.check?.name && !context?.check?.value) {
          region.selectStatus = EMPTY;
          childrenSelectList = []
        }
        // 同步 activeItem
        if (activeItem?.id === region?.id) {
          setActiveItem({
            ...activeItem,
            childrenSelectList,
          });
        }

        return {
          ...region,
          childrenSelectList,
          // selectStatus,
        };
      });
      setRenderList({
        ...renderList,
        regions: newRegions,
      });

      setSelectList({
        ...selectList,
        regions,
        global,
      });
    },
    [activeItem, renderList, selectList],
  );

  // markets selectList 变化
  const marketsSelectListOnChange = useCallback(
    (marketsSelectList: any[], parentId?: string, allMarkets?: string[]) => {
      if (!parentId) return;
      let needInit = true

      renderList?.regions?.forEach((region: any) => {
        // 根据 markets 的变化联动 regions
        if (parentId && region?.id === parentId) {
          region.childrenSelectList = marketsSelectList;
          // Unidentified的情况时直接全选
          if (parentId == 'Unidentified') {
            region.selectStatus = ALL;
            return
          }
          changeSelectStatus(region, marketsSelectList);
        }
        // 同步 activeItem
        if (region?.id === activeItem?.id && parentId === activeItem?.id) {
          activeItem.childrenSelectList = marketsSelectList;
          changeSelectStatus(activeItem, marketsSelectList);
        }
        // Unidentified 没有取消时不做初始化处理
        if (region?.childrenSelectList?.length > 0 || (region?.id === 'Unidentified' && region?.selectStatus === 'all')) needInit = false
      });

      // clear all 之后初始化处理
      if (needInit) {
        init('', true)
        return
      }
      setRenderList({ ...renderList });
      setActiveItem({ ...activeItem });
      if (marketsSelectList?.length > 0) {
        setSelectList({
          ...selectList,
          global: '',
          regions: renderList?.regions
            ?.filter((region: any) => region?.selectStatus !== HALF && region?.selectStatus !== EMPTY)
            ?.map((region: any) => region?.id),
          markets: allMarkets
        });
      }
    },
    [activeItem, renderList, selectList],
  );

  // 受控
  const changeWithOutSideValue = (value?: string[]) => {
    if (value && (value[0] === GLOBAL.toLocaleLowerCase() || value[0] === GLOBAL_NO_CHINA.toLocaleLowerCase())) {
      init(value[0]);
      return;
    }
    // 初始化过后才能继续
    if (!initFlag || !value) return;
    if (regionType === 'region') {
      regionSelectListOnChange(value);
    } else if (regionType === 'market') {
      const regionMarketMap: any = [];
      // 当有unidentified的情况
      if (value?.includes('Unidentified')) regionMarketMap['Unidentified'] = [];
      // 找出传入的 market 是属于哪个 region
      renderList?.regions?.forEach((region: any) => {
        region.children?.forEach((market: any) => {
          if (!value?.includes(market?.id)) return;
          if (!regionMarketMap[region?.id]) {
            regionMarketMap[region?.id] = [];
          }
          regionMarketMap[region?.id].push(market?.id);
        });
      });

      for (const regionId in regionMarketMap) {
        if (!Object.prototype.hasOwnProperty.call(regionMarketMap, regionId))
          continue;
        setTimeout(() => {
          marketsSelectListOnChange(regionMarketMap[regionId], regionId, value);
        }, 500);
      }
    }
  };

  useEffect(() => {
    setActiveTabId(activeTab)
  }, [activeTab])

  useEffect(() => {
    onTabChange?.(activeTabId)
  }, [activeTabId])

  useEffect(() => {
    const halfSelectLength = renderList?.regions?.filter(
      (region: any) => region?.selectStatus === HALF,
    );

    if (selectList?.regions?.length <= 0 && halfSelectLength?.length <= 0) {
      setSelectList({
        ...selectList,
        global: selectList?.global || GLOBAL,
      });
    }
  }, [selectList?.regions?.length]);

  // 初始化
  useEffect(() => {
    init();
  }, [activeTabId, regions, recentList]);

  // 监听清除
  useEffect(() => {
    if (!deleteFlag?.delete) return;
    init();
  }, [deleteFlag]);

  // 设置默认值
  useEffect(() => {
    if (isSetDefault) return;
    if (renderList?.regions?.length > 0) {
      setIsSetDefault(true);
      changeWithOutSideValue(defaultValue);
    }
  }, [initFlag, isSetDefault, renderList?.regions?.length]);

  // 监听 value
  useEffect(() => {
    if (!value || renderList?.regions?.length <= 0) return;
    changeWithOutSideValue(value);
  }, [value, initFlag, renderList?.regions?.length]);

  // 最终结果
  useEffect(() => {
    let regions: any[] = (() => {
      let regionResult = (selectList?.regions && [...selectList?.regions]) || [];

      // 挑选出半选的数据
      renderList?.regions?.forEach((region: any) => {
        if (
          region?.selectStatus === HALF &&
          !selectList?.regions?.includes(region?.id)
        ) {
          regionResult.push(region?.id);
        }
      });

      return regionResult;
    })();
    let markets: any[] = [];
    let tagList: any[] = [];

    if (activeTabId === COMMON) {
      // 筛选出已选 region 下的 markets
      if (!selectList?.global) {
        renderList?.regions?.forEach((region: any) => {
          if (region?.childrenSelectList?.length > 0) {
            markets = [...markets, ...region?.childrenSelectList];
          }
        });
      } else {
        regions = [selectList?.global];
      }
    } else {
      markets = selectList?.markets;
    }

    tagList = renderList?.regions
      ?.map((region: any) => {
        const list =
          region?.children?.filter((markets: any) =>
            region?.childrenSelectList?.includes(markets?.id),
          ) || [];
        return list;
      })
      .flat();

    setResult({
      regions,
      markets,
      tagList,
    });
    // 将结果传出
    onChange?.({
      regions,
      markets,
      renderList
    });
  }, [selectList]);

  return {
    tabs,
    result,
    renderList,
    selectList,
    recentList,
    activeItem,
    activeTabId,
    searchValue,
    search,
    setActiveTabId,
    tagOnDelete,
    activeItemOnChange,
    regionSelectListOnChange,
    globalSelectOnChange,
    marketsSelectListOnChange,
    recentListOnChange,
  };
};

export default useData;
